import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { X, ExternalLink } from 'lucide-react';

// Full-screen image viewer with scroll-to-zoom and drag-to-pan, portaled to
// document.body (any ancestor with a transform/filter/backdrop-filter — e.g.
// the .glass-card look used across this app — becomes a containing block for
// position:fixed descendants, which would otherwise trap this inside it
// instead of the real viewport).
const ImageLightbox = ({ src, alt, fullPageUrl, onClose }) => {
    const [zoomScale, setZoomScale] = useState(1);
    const [zoomOffset, setZoomOffset] = useState({ x: 0, y: 0 });
    const dragStateRef = useRef(false);

    useEffect(() => {
        setZoomScale(1);
        setZoomOffset({ x: 0, y: 0 });
    }, [src]);

    useEffect(() => {
        const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    const handleWheel = (e) => {
        e.preventDefault();
        setZoomScale(prev => {
            const next = Math.min(4, Math.max(1, prev - e.deltaY * 0.0015));
            if (next === 1) setZoomOffset({ x: 0, y: 0 });
            return next;
        });
    };

    // Tracked on window rather than the image itself — once zoomed in, a fast
    // drag easily carries the cursor past the image's own bounds, and an
    // element-scoped mousemove/mouseup would silently stop following.
    const handleMouseDown = (e) => {
        e.stopPropagation();
        if (zoomScale <= 1) return;
        const startX = e.clientX;
        const startY = e.clientY;
        const originX = zoomOffset.x;
        const originY = zoomOffset.y;
        dragStateRef.current = true;

        const handleMove = (moveEvent) => {
            setZoomOffset({ x: originX + (moveEvent.clientX - startX), y: originY + (moveEvent.clientY - startY) });
        };
        const handleUp = () => {
            dragStateRef.current = false;
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', handleUp);
        };
        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleUp);
    };

    const handleDoubleClick = (e) => {
        e.stopPropagation();
        setZoomScale(1);
        setZoomOffset({ x: 0, y: 0 });
    };

    return ReactDOM.createPortal(
        <div
            className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
        >
            <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/70 rounded-lg text-white transition-colors"
            >
                <X className="w-6 h-6" />
            </button>

            <img
                src={src}
                alt={alt || 'Image'}
                className={`max-w-full max-h-full object-contain select-none ${zoomScale > 1 ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-in'}`}
                style={{
                    transform: `scale(${zoomScale}) translate(${zoomOffset.x / zoomScale}px, ${zoomOffset.y / zoomScale}px)`,
                    transition: dragStateRef.current ? 'none' : 'transform 150ms ease-out'
                }}
                onClick={(e) => e.stopPropagation()}
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onDoubleClick={handleDoubleClick}
                onContextMenu={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
            />

            <span className="absolute top-4 left-4 px-3 py-1.5 bg-black/50 backdrop-blur-sm rounded-lg text-white/70 text-xs">
                Scroll to zoom · drag to pan · double-click to reset
            </span>

            {fullPageUrl && (
                <Link
                    to={fullPageUrl}
                    onClick={(e) => e.stopPropagation()}
                    className="absolute bottom-4 right-4 flex items-center gap-2 px-4 py-2 bg-black/60 hover:bg-black/80 backdrop-blur-sm rounded-lg text-white text-sm font-medium transition-colors"
                >
                    <ExternalLink className="w-4 h-4" />
                    View full image page
                </Link>
            )}
        </div>,
        document.body
    );
};

export default ImageLightbox;
